import { SearchPageChrome } from "@/components/search/page-chrome"
import {
  BookOpen,
  BookOpenText,
  BookText,
  Clock,
  ExternalLink,
  Globe,
  GraduationCap,
  Heart,
  RefreshCw,
  Star,
  TvMinimalPlay,
  Users,
} from "lucide-react"
import { useId, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Namespace, Resources, TFunction } from "i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { PageBody } from "@/components/ui/layout"
import { cn } from "@/lib/utils"
import { typedEntries } from "@/types/helpers"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayDay,
  displayPlainTime,
} from "@/lib/timetableZonedDatetime"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { SelectOption } from "@/components/ui/combobox"
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Checkbox } from "@/components/ui/checkbox"
import { useGetColours } from "@/lib/useGetColours"
import {
  EmbeddedSheetPage,
  SheetPage,
  SheetPageTrigger,
} from "@/components/ui/sheet-page"
import { ActionButton } from "@/components/shared/action-buttons"
import { ShortlistMembership } from "@/components/search/shortlists/shortlist-membership"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { CycleCalendar } from "@/components/shared/cycle-calendar"
import { Scope } from "@/lib/page-rendering"
import { ShowProps, useShowProps } from "./page-props"

export default function Page() {
  const { result, shortlists } = useShowProps()

  return (
    <>
      <SearchPageChrome
        identity={{ name: result.class_name }}
        action={<Actions />}
      />
      <PageBody>
        <ShortlistMembership shortlists={shortlists} />
        <Card>
          <CardHeader className="flex flex-col gap-4">
            <CardTitle className="flex items-center"></CardTitle>
            <ClassDetails />
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4">
            <ListingDetails />
            <SpecialityDetails />
            <TimetableSessionDetails />
            <LiveClassesGrid />
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

function Actions() {
  const { t } = useTranslation("search_listing", { keyPrefix: "actions" })
  const { result, capabilities } = useShowProps()

  if (!capabilities.can_shortlist) return null

  return (
    <EmbeddedSheetPage
      href={`/search/classes/${result.uuid}/shortlist_entries/new`}
      scope={addClassToShortlist}
    >
      <SheetPageTrigger asChild>
        <ActionButton Icon={Heart} variant="solid">
          {t("shortlist_class")}
        </ActionButton>
      </SheetPageTrigger>
    </EmbeddedSheetPage>
  )
}

// The add sheets accept only their add page. A rejected submission re-renders
// it in the sheet with its errors; a successful one resolves to a show page,
// which the sheet hands to the page beneath and closes.
const addClassToShortlist: Scope = (resolved) =>
  resolved.name === "search/classes/shortlist_entries/new"

const addEducatorToShortlist: Scope = (resolved) =>
  resolved.name === "search/educators/shortlist_entries/new"

function ClassDetailContainer({
  children,
  className,
  ...rest
}: React.ComponentProps<"div">) {
  return (
    <div className={cn("grid grid-cols-1", className)} {...rest}>
      {children}
    </div>
  )
}

function ClassDetailHeader({
  className,
  children,
  ...rest
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "text-muted-foreground flex flex-row items-center gap-1 [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

function ClassDetails() {
  const { result: classDetails } = useShowProps()
  const { t } = useTranslation("search_listing", { keyPrefix: "details" })
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  return (
    <div className="flex flex-col gap-2">
      <span className="px-4 text-xl font-semibold">{t("label")}</span>
      <div className="grid grid-cols-3 justify-between gap-2 px-4">
        <ClassDetailContainer>
          <ClassDetailHeader>
            <BookText />
            <span>{t("curriculum")}</span>
          </ClassDetailHeader>
          <span>
            {tCurricula(
              classDetails.curriculum_key as keyof Resources["subjects"]["curricula"],
            )}
          </span>
        </ClassDetailContainer>

        <ClassDetailContainer>
          <ClassDetailHeader>
            <BookOpen />
            <span>{t("subject_name")}</span>
          </ClassDetailHeader>
          <div className="flex flex-row items-center gap-1">
            <span>
              {tSubjects(
                classDetails.subject_key as keyof Resources["subjects"]["subjects"],
              )}
            </span>
            <Badge variant={"secondary"} className="text-sm">
              {classDetails.subject_code}
            </Badge>
          </div>
        </ClassDetailContainer>
        <ClassDetailContainer>
          <ClassDetailHeader>
            <Globe />
            <span>{t("timezone_id")}</span>
          </ClassDetailHeader>
          <span>{classDetails.timezone_id}</span>
        </ClassDetailContainer>

        <ClassDetailContainer>
          <ClassDetailHeader>
            <Users />
            <span>{t("class_size.label")}</span>
          </ClassDetailHeader>
          <span>
            {t("class_size", {
              min: classDetails.min_students,
              max: classDetails.max_students,
            })}
          </span>
        </ClassDetailContainer>

        <ClassDetailContainer>
          <ClassDetailHeader>
            <RefreshCw />
            <span>{t("cycle_length.label")}</span>
          </ClassDetailHeader>
          <span>
            {t("cycle_length", { count: classDetails.cycle_length / 7 })}
          </span>
        </ClassDetailContainer>

        <ClassDetailContainer>
          <ClassDetailHeader>
            <Clock />
            <span>{t("num_classes.label")}</span>
          </ClassDetailHeader>
          <span>{t("num_classes", { count: classDetails.num_classes })}</span>
        </ClassDetailContainer>

        <SessionDetails
          session={classDetails.live_sessions_attributes}
          type="live_sessions"
        />
        <SessionDetails
          session={classDetails.student_led_sessions_attributes}
          type="student_led_sessions"
        />
        <SessionDetails
          session={classDetails.other_sessions_attributes}
          type="other_sessions"
        />
      </div>
    </div>
  )
}

function SessionDetails({
  session,
  type,
}: {
  session:
    | ShowProps["result"]["live_sessions_attributes"]
    | ShowProps["result"]["other_sessions_attributes"]
    | ShowProps["result"]["student_led_sessions_attributes"]
  type: "live_sessions" | "other_sessions" | "student_led_sessions"
}) {
  const { t } = useTranslation("search_listing", { keyPrefix: "details" })
  const getSessionIcon = (
    sessionType: "live_sessions" | "other_sessions" | "student_led_sessions",
  ) => {
    switch (sessionType) {
      case "live_sessions":
        return <Clock />
      case "other_sessions":
        return <BookOpenText />
      case "student_led_sessions":
        return <GraduationCap />
      default:
        return null
    }
  }
  return (
    <ClassDetailContainer>
      <ClassDetailHeader>
        {getSessionIcon(type)}
        <span>{t(`${type}.label`)}</span>
      </ClassDetailHeader>
      {session.length === 0 ? (
        <span>{t(`${type}.none`)}</span>
      ) : (
        session.map((session, index) => (
          <span key={index}>
            {t(type, { count: session.count, duration: session.duration })}
          </span>
        ))
      )}
    </ClassDetailContainer>
  )
}

function ListingDetails() {
  const {
    result: { listing_profile: listingProfile },
  } = useShowProps()
  const { t } = useTranslation("search_listing", {
    keyPrefix: "listing_details",
  })
  return (
    <Card className=" ">
      <CardHeader className="flex flex-row flex-wrap justify-between pb-0">
        <div className="flex flex-col space-y-1.5">
          <CardTitle>{listingProfile.name}</CardTitle>
          <CardDescription className="text-base" data-testid="listing-type">
            {t(listingProfile.listing_type)}
          </CardDescription>
        </div>
        <div className="flex flex-col-reverse gap-2 md:flex-row">
          {listingProfile.video_url ? (
            <VideoDialog videoUrl={listingProfile.video_url} />
          ) : null}
          {listingProfile.webpage_url ? (
            <a
              className={cn(
                buttonVariants({
                  variant: "link",
                  className: "pl-0",
                }),
              )}
              href={listingProfile.webpage_url}
              target="_blank"
              rel="noopener"
            >
              {t("webpage_link")}
              <ExternalLink />
            </a>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 overflow-auto">
        {listingProfile.written_comment ? (
          <>
            <div className="overflow-auto">
              <Label className="mb-2">{t("written_comment.label")}</Label>
              <p className="whitespace-pre-wrap">
                {listingProfile.written_comment}
              </p>
            </div>
          </>
        ) : null}

        <TeachingDetails />
      </CardContent>
    </Card>
  )
}

function ShortlistTeacher({
  teacher,
}: {
  teacher: ShowProps["result"]["teachers"][number] | null
}) {
  const { result } = useShowProps()
  const listingProfileId = result.listing_profile.uuid
  const name = teacher?.name || result.listing_profile.name

  const { t } = useTranslation("search_listing", {
    keyPrefix: "teaching_details",
  })

  return (
    <EmbeddedSheetPage
      href={`/search/educators/${teacher ? teacher.uuid : listingProfileId}/shortlist_entries/new`}
      scope={addEducatorToShortlist}
    >
      <SheetPageTrigger asChild>
        <Button
          className="flex-wrap"
          aria-label={t("shortlist_teacher", { teacherName: name })}
        >
          {t("shortlist")}
          <Heart />
        </Button>
      </SheetPageTrigger>
    </EmbeddedSheetPage>
  )
}

function TeacherActions({
  teacher,
}: {
  teacher: ShowProps["result"]["teachers"][number] | null
}) {
  const { capabilities } = useShowProps()

  if (capabilities.can_shortlist) return <ShortlistTeacher teacher={teacher} />

  return null
}

function ShortlistTeam() {
  const { result } = useShowProps()
  const listingProfileId = result.listing_profile.uuid
  const name = result.listing_profile.name

  const { t } = useTranslation("search_listing", {
    keyPrefix: "listing_details",
  })

  return (
    <EmbeddedSheetPage
      href={`/search/educators/${listingProfileId}/shortlist_entries/new`}
      scope={addEducatorToShortlist}
    >
      <SheetPageTrigger asChild>
        <Button
          className="flex-wrap"
          aria-label={t("shortlist_team", { teamName: name })}
        >
          {t("shortlist")}
          <Heart />
        </Button>
      </SheetPageTrigger>
    </EmbeddedSheetPage>
  )
}

function TeamActions() {
  const { capabilities } = useShowProps()

  if (capabilities.can_shortlist) return <ShortlistTeam />

  return null
}

function TeachingDetails() {
  const { t } = useTranslation("search_listing", {
    keyPrefix: "teaching_details",
  })
  const { result } = useShowProps()
  const teachers =
    result.listing_profile.listing_type === "personal" ||
    result.teachers.length === 0
      ? [
          <div className="flex flex-wrap justify-between" key="personal">
            <div className="flex flex-wrap">
              <SearchDetailLink
                href={result.listing_profile.profile_url}
                className={cn(
                  buttonVariants({
                    variant: "link",
                    className: "pl-0 text-lg",
                  }),
                )}
              >
                {result.listing_profile.name}
              </SearchDetailLink>
              {result.listing_profile.listing_type === "personal" && (
                <TeacherActions teacher={null} />
              )}
              {result.listing_profile.listing_type === "team" && (
                <TeamActions />
              )}
            </div>
          </div>,
        ]
      : result.teachers.map((teacher) => (
          <div className="flex flex-wrap justify-between" key={teacher.id}>
            <div className="flex flex-wrap">
              <SearchDetailLink
                href={teacher.profile_url}
                className={cn(
                  buttonVariants({
                    variant: "link",
                    className: "pl-0 text-lg",
                  }),
                )}
              >
                <Avatar className="border">
                  <AvatarImage
                    src={teacher.photo_id || undefined}
                    alt={teacher.name}
                  />
                  <AvatarFallback>??</AvatarFallback>
                </Avatar>
                {teacher.name}
              </SearchDetailLink>
              <span className="inline-flex items-center gap-1">
                <Star className="size-5 fill-yellow-400 stroke-yellow-600" />
                {teacher.average_feedback_rating} / 5
              </span>
            </div>
            <TeacherActions teacher={teacher} />
          </div>
        ))

  return (
    <div className="flex flex-col gap-1" data-testid="class-teaching-details">
      <span className="text-xl font-semibold">{t("label")}</span>
      <div className="grid grid-cols-1 gap-4">{teachers}</div>
    </div>
  )
}

function VideoDialog({ videoUrl }: { videoUrl: string }) {
  const { t } = useTranslation("search_listing")

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={"outline"} className="flex-1">
          <TvMinimalPlay />
          {t("video.trigger.label")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("video.trigger.label")}</DialogTitle>
        </DialogHeader>
        <div className="h-36 w-full rounded-xl bg-black"></div>
        {videoUrl}
      </DialogContent>
    </Dialog>
  )
}

type Range =
  | {
      from: number
      to: number
    }
  | {
      from: keyof Resources["search"]["labels"]
      to: keyof Resources["search"]["labels"]
    }

type FormatValue =
  | string
  | boolean
  | string[]
  | Range
  | {
      [key: string]: Range | string
    }

function SpecialityDetails() {
  const {
    result: { specialities },
  } = useShowProps()
  const { t } = useTranslation(["search_listing", "common", "search"])

  if (specialities.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("speciality.label")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-2 divide-y md:grid-cols-2 md:divide-x md:divide-y-0">
          {specialities.map((speciality, index) => {
            const { name, ...rest } = speciality
            const specialityData = typedEntries(rest) as Array<
              [string, FormatValue]
            >

            return (
              <div key={index} className="px-4 only:col-span-2">
                <span className="text-lg font-medium">{name}</span>
                <div className="space-y-3">
                  {specialityData.map(([key, value]) => (
                    <div key={key} className="flex flex-col gap-2">
                      <span className="text-muted-foreground font-medium">
                        {(t as (translationKey: string) => string)(
                          `search_listing:attributes.${key}.label`,
                        )}
                        :
                      </span>
                      <Speciality value={value as FormatValue} parent={key} />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function Speciality({ value, parent }: { value: FormatValue; parent: string }) {
  const { t } = useTranslation(["search_listing", "common", "search"])
  const { t: tReligionFacet } = useTranslation("religions", {
    keyPrefix: "religions",
  })
  const { t: tLanguageFacet } = useTranslation("languages", {
    keyPrefix: "languages",
  })
  const { t: tGenderFacet } = useTranslation("genders", {
    keyPrefix: "genders",
  })

  const formattedValue = [
    "adhd",
    "autism",
    "depression",
    "learning_preference_group",
    "learning_preference_diverse_students",
    "learning_preference_like_students",
    "learning_preference_individual",
  ].includes(parent)
    ? internationaliseValue(
        value as
          | keyof Resources["search"]["labels"]
          | (keyof Resources["search"]["labels"])[],
        t,
      )
    : value

  if (formattedValue === null) return

  if (Array.isArray(formattedValue)) {
    if (
      parent === "speciality_primary_languages" ||
      parent === "speciality_secondary_languages"
    ) {
      return (
        <ArraySpeciality
          arr={value as string[]}
          parent={parent}
          t={tLanguageFacet}
        />
      )
    }

    if (parent === "speciality_genders") {
      return (
        <ArraySpeciality
          arr={value as string[]}
          parent={parent}
          t={tGenderFacet}
        />
      )
    }

    if (parent === "speciality_religions") {
      return (
        <ArraySpeciality
          arr={value as string[]}
          parent={parent}
          t={tReligionFacet}
        />
      )
    }

    return <ArraySpeciality arr={formattedValue} parent={parent} />
  }

  if (typeof formattedValue === "boolean") {
    return (
      <span>
        {value ? t("common:boolean.true") : t("common:boolean.false")}
      </span>
    )
  }

  if (
    typeof formattedValue === "object" &&
    formattedValue !== null &&
    "from" in formattedValue &&
    "to" in formattedValue
  ) {
    return <RangeSpeciality range={formattedValue as Range} />
  }

  if (typeof formattedValue === "object" && formattedValue !== null) {
    const entries = typedEntries(formattedValue)

    return entries.map(([key, formattedValue]) => {
      if (formattedValue === null) return null
      return (
        <div key={key} className="contents">
          {parent === key ? null : (
            <span className="text-muted-foreground">
              {t(`search:facet.${key as keyof Resources["search"]["facet"]}`)}:
            </span>
          )}
          <Speciality value={formattedValue} parent={key.toString()} />
        </div>
      )
    })
  }

  return <span>{String(formattedValue)}</span>
}

function ArraySpeciality({
  arr,
  t: tSpeciality,
  parent,
}: {
  arr: string[]
  t?: TFunction<any>
  parent: string
}) {
  const { t } = useTranslation(["search_listing", "common", "search"])
  return (
    <ul className="list-inside list-disc">
      <span className="text-muted-foreground">
        {t(`search:facet.${parent as keyof Resources["search"]["facet"]}`)}:
      </span>
      {arr.map((i, index) => (
        <li key={index}>{tSpeciality ? tSpeciality(i) : i}</li>
      ))}
    </ul>
  )
}

function RangeSpeciality({ range }: { range: Range }) {
  const { t } = useTranslation(["search_listing", "common", "search"])
  const formattedRange = useMemo(() => {
    const { from, to } = range
    if (typeof from === "number") {
      return { from: String(from), to: String(to) }
    }
    return { from: t(`search:labels.${from}`), to: t(`search:labels.${to}`) }
  }, [range, t])
  return (
    <div className="flex flex-row items-center gap-4">
      <span className="truncate">{formattedRange.from}</span>
      <span className="text-muted-foreground text-sm">
        {t("search:facet.range_separator")}
      </span>
      <span className="truncate">{formattedRange.to}</span>
    </div>
  )
}

function internationaliseValue(
  value:
    | keyof Resources["search"]["labels"]
    | (keyof Resources["search"]["labels"])[],
  tFunction: TFunction<Namespace>,
) {
  if (Array.isArray(value)) {
    return value.map((v) => tFunction(`search:labels.${v}`))
  }
  return tFunction(`search:labels.${value}`)
}

function LiveClassesGrid() {
  const { result } = useShowProps()
  const { t } = useTranslation("search_listing")
  const titleId = useId()
  if (result.live_classes.length === 0) return null

  const liveClasses = result.live_classes.flatMap((livePriority) => {
    const priorityDetails = result.timetable_priorities.find(
      (priority) => priority.uuid === livePriority.priority_uuid,
    )
    if (!priorityDetails) return []

    return {
      sessions: priorityDetails.sessions,
      priority: priorityDetails.priority,
      ...livePriority,
    }
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>{t("live_classes.label")}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul
          className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
          aria-labelledby={titleId}
        >
          {liveClasses.map((liveClass) => (
            <Card
              role="listitem"
              aria-labelledby={`${liveClass.priority_uuid}-${liveClass.instance_index}-title`}
              key={`${liveClass.priority_uuid}-${liveClass.instance_index}`}
            >
              <CardHeader className="pb-2">
                <CardTitle
                  className="text-lg"
                  id={`${liveClass.priority_uuid}-${liveClass.instance_index}-title`}
                >
                  {t("live_classes.title", {
                    class_name: result.class_name,
                    priority: liveClass.priority,
                    instance: liveClass.instance_index + 1,
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("live_classes.student_count")}
                  </span>
                  <span>{liveClass.student_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("live_classes.available_space")}
                  </span>
                  <span>{liveClass.available_space}</span>
                </div>

                {liveClass.sessions.length > 0 && (
                  <div className="border-t pt-2">
                    <span className="text-muted-foreground text-sm">
                      {t("live_classes.timetable_sessions")}
                    </span>
                    <ul className="mt-1 space-y-1 text-sm">
                      {liveClass.sessions.map((session, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Clock className="text-muted-foreground size-3" />
                          <span>
                            {t("timetable.schedule.fixed", {
                              day: displayDay(session.day ?? 0),
                              start: displayPlainTime(
                                Temporal.PlainTime.from(
                                  session.start_time ?? "00:00",
                                ),
                              ),
                              end: displayPlainTime(
                                Temporal.PlainTime.from(
                                  session.start_time ?? "00:00",
                                ).add({
                                  minutes: session.duration,
                                }),
                              ),
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {liveClass.requestable && (
                  <RequestToJoinButton
                    className={result.class_name}
                    selectedInstanceIndex={liveClass.instance_index}
                    selectedPriorityUuid={liveClass.priority_uuid}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function RequestToJoinButton({
  selectedPriorityUuid,
  selectedInstanceIndex,
  className,
}: {
  selectedPriorityUuid: string
  selectedInstanceIndex: number
  className: string
}) {
  const {
    capabilities: { change_wishlist_item_href: href },
  } = useShowProps()
  const { t } = useTranslation("search_listing")

  if (!href) return null

  return (
    <SheetPage
      href={href}
      data={{
        priority_uuid: selectedPriorityUuid,
        instance_index: selectedInstanceIndex,
        class_name: className,
      }}
    >
      <SheetPageTrigger asChild>
        <Button className="w-full">{t("live_classes.request_to_join")}</Button>
      </SheetPageTrigger>
    </SheetPage>
  )
}

function TimetableSessionDetails() {
  const {
    result: { timetable_sessions: timetableSessions },
  } = useShowProps()
  const [calendarRef] = useFullCalendarApi("SearchResultTimetableSessions")
  const { result } = useShowProps()
  const listingType = result.listing_profile.listing_type
  const cycleLength = result.cycle_length
  const { t } = useTranslation("search_listing")

  const getColours = useGetColours()
  const sessionTeacherMap = useMemo<
    Map<number, ShowProps["result"]["teachers"][number]>
  >(() => {
    const map = new Map<number, ShowProps["result"]["teachers"][number]>()
    result.listed_class_offerings.forEach((classOffering) => {
      classOffering.timetable_session_ids.forEach((sessionId) => {
        map.set(
          sessionId,
          result.teachers.find(
            (teacher) => String(teacher.id) === classOffering.teacher_id,
          )!,
        )
      })
    })
    return map
  }, [result.listed_class_offerings, result.teachers])

  const [selectedClassOfferings, setSelectedClassOfferings] = useState<
    SelectOption<string>[]
  >(
    result.listed_class_offerings.map((classOffering) => ({
      value: classOffering.teacher_id,
      label: classOffering.teacher_name,
    })),
  )

  const [selectedSessionIds, setSelectedSessionIds] = useState<number[]>(
    timetableSessions.map((session) => session.id),
  )

  function updateSelectedSessionIds(
    selectedClassOfferings: SelectOption<string>[],
  ) {
    if (result.listed_class_offerings.length > 0) {
      setSelectedSessionIds(
        selectedClassOfferings.flatMap(
          ({ value }) =>
            result.listed_class_offerings.find(
              ({ teacher_id }) => teacher_id === value,
            )?.timetable_session_ids || [],
        ),
      )
    }
  }

  function handleSelectedTeachersChanged(selectedTeachers: SelectOption[]) {
    setSelectedClassOfferings(selectedTeachers)
    updateSelectedSessionIds(selectedTeachers)
  }

  const calendarTimezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
  const calendarStartInstant = Temporal.Instant.from("2025-01-06T00:00:00Z")

  const events = timetableSessions
    .filter((session) => selectedSessionIds.includes(session.id))
    .map(
      (
        { day, start_time, duration, priority, id, timetable_priority_id },
        index,
      ) => {
        const educator = sessionTeacherMap.get(id)

        return {
          cycleLength: result.cycle_length,
          startTime: start_time!,
          duration,
          day: day!,
          id: timetable_priority_id,
          eventTimezoneId: result.timezone_id,
          eventTitle: educator?.abbr
            ? t("timetable.events.short_title", {
                teacherInitials: educator?.abbr,
                priority,
              })
            : t("timetable.events.short_title_without_initials", {
                priority,
              }),
          extendedProps: {
            timetableSessionIndex: index,
            colours: getColours(`${timetable_priority_id}`, 0, 0),
          },
        }
      },
    )
    .flat()

  return (
    <Card className="flex grow flex-col">
      <CardHeader>
        <CardTitle>{t("timetable.label")}</CardTitle>
      </CardHeader>
      <CardContent className="relative w-full grow">
        <div className="flex h-full w-full flex-row gap-4">
          {listingType !== "personal" && result.teachers.length > 1 ? (
            <div className="h-full w-80">
              <SelectedTeachers
                selectedTeachers={selectedClassOfferings}
                teachers={result.teachers.map((teacher) => ({
                  value: String(teacher.id),
                  label: teacher.name,
                }))}
                onSelectedTeachersChanged={handleSelectedTeachersChanged}
              />
            </div>
          ) : null}

          <div
            className="h-full w-full"
            style={
              {
                "--fc-today-bg-color": "transparent",
              } as React.CSSProperties
            }
          >
            <CycleCalendar
              events={events}
              cycleLength={cycleLength}
              interactive={false}
              calendarRef={calendarRef}
              aspectRatio={1.5}
              height={undefined}
              calendarTimezoneId={calendarTimezoneId}
              calendarStartInstant={calendarStartInstant}
              eventContent={({
                event: {
                  title,
                  extendedProps: { timetableSessionIndex },
                },
              }) => {
                const session = timetableSessions[timetableSessionIndex]
                if (!session) return null
                const { day, start_time, duration, priority, id } = session
                return (
                  <>
                    <Event
                      teacher_id={sessionTeacherMap.get(id)?.id || null}
                      day={day!}
                      start_time={start_time!}
                      duration={duration}
                      priority={priority}
                      title={title}
                      eventTimezoneId={result.timezone_id}
                      calendarTimezoneId={calendarTimezoneId}
                    />
                  </>
                )
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function Event({
  teacher_id,
  day,
  start_time,
  duration,
  priority,
  title,
  eventTimezoneId,
  calendarTimezoneId,
}: {
  teacher_id: string | null
  day: number
  start_time: string
  duration: number
  priority: number
  title: string
  eventTimezoneId: string
  calendarTimezoneId: string
}) {
  const { result } = useShowProps()

  const startTime = calculateEventStartCalendarTimezoneCoerced({
    startTime: start_time,
    day: day,
    calendarTimezoneId,
    eventTimezoneId,
    startTemporalZonedDateTime: Temporal.Instant.from(
      "2025-01-06T00:00:00Z",
    ).toZonedDateTimeISO("UTC"),
  })
  const { t } = useTranslation("search_listing")

  return (
    <div className="fc-event-main-frame h-full">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className={cn(
              buttonVariants({ variant: "link" }),
              "fc-event-title fc-stick top-0 m-0 h-min w-full justify-start p-0 text-xs text-current ring-current!",
            )}
          >
            {title}
          </TooltipTrigger>
          <TooltipContent className="text-white">
            {teacher_id && (
              <>
                <strong>
                  {
                    result.teachers.find(
                      (teacher) => teacher.id === teacher_id,
                    )!.name
                  }
                </strong>
                <br />
              </>
            )}
            {t("timetable.schedule.priority", { priority })}
            <br />
            {t("timetable.schedule.duration", { duration })}
            <br />
            {t("timetable.schedule.fixed", {
              day: displayDay(day),
              start: displayPlainTime(startTime.toPlainTime()),
              end: displayPlainTime(
                startTime.add({ minutes: duration }).toPlainTime(),
              ),
            })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

const isSelectedPred = (teacher: SelectOption) => (selected: SelectOption) =>
  selected.value === teacher.value
const isNotSelectedPred = (teacher: SelectOption) => (selected: SelectOption) =>
  selected.value !== teacher.value

function SelectedTeachers({
  teachers,
  selectedTeachers,
  onSelectedTeachersChanged,
}: {
  teachers: Array<SelectOption<string>>
  selectedTeachers: Array<SelectOption<string>>
  onSelectedTeachersChanged: (selectedTeachers: SelectOption<string>[]) => void
}) {
  const { t } = useTranslation("search_listing")

  function selectAll() {
    onSelectedTeachersChanged(teachers)
  }

  function clear() {
    onSelectedTeachersChanged([])
  }

  function toggle(teacher: SelectOption) {
    onSelectedTeachersChanged(
      selectedTeachers.some(isSelectedPred(teacher))
        ? selectedTeachers.filter(isNotSelectedPred(teacher))
        : [...teachers, teacher],
    )
  }

  const id = useId()

  return (
    <>
      <div id={id} className="text-lg font-semibold">
        {t("timetable.teacher_selector.label")}
      </div>
      <Command className="gap-2" aria-labelledby={id}>
        <div className="sticky space-y-2">
          <div className="flex w-full flex-row flex-wrap gap-2">
            <Button
              size={"sm"}
              variant={"secondary"}
              className="w-full"
              onClick={selectAll}
            >
              {t("timetable.list.select_all_teachers")}
            </Button>
            <Button
              size={"sm"}
              variant={"destructive-outline"}
              className="w-full"
              onClick={clear}
            >
              {t("timetable.list.clear_all")}
            </Button>
          </div>
          <CommandInput
            placeholder={t("timetable.teacher_selector.placeholder")}
          />
        </div>
        <CommandList className="max-h-100" label={t("timetable.list.label")}>
          {teachers.map((teacher) => (
            <CommandItem
              data-slot="command-item"
              className="hover:bg-muted justify-between data-[selected='true']:bg-[none] [&_svg:not([class*='text-'])]:text-white"
              key={teacher.value}
              value={String(teacher.value + 1)}
              onSelect={() => toggle(teacher)}
              keywords={[teacher.label]}
            >
              {teacher.label}
              <Checkbox
                checked={selectedTeachers.some(isSelectedPred(teacher))}
              />
            </CommandItem>
          ))}
        </CommandList>
      </Command>
    </>
  )
}
